/**
 * Lead persistence.
 *
 * Forms still POST to their external endpoints (VITE_MASTERCLASS_URL /
 * VITE_STRATEGY_SESSION_URL). This module additionally stores each submission
 * in the backend so `/admin/leads` can show status, timestamps and the full
 * UTM/attribution trail. Storage failures never block the user — the external
 * endpoint is still the system of record for delivery.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

type Payload = Record<string, unknown>;

const str = (v: unknown, max = 500): string | null => {
  if (typeof v === "number") return String(v);
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t ? t.slice(0, max) : null;
};

/** Columns both tables share — every UTM / click-id / referrer field. */
function attributionColumns(p: Payload) {
  return {
    utm_source: str(p.utm_source, 200),
    utm_medium: str(p.utm_medium, 200),
    utm_campaign: str(p.utm_campaign, 200),
    utm_term: str(p.utm_term, 200),
    utm_content: str(p.utm_content, 200),
    ft_utm_source: str(p.ft_utm_source, 200),
    ft_utm_medium: str(p.ft_utm_medium, 200),
    ft_utm_campaign: str(p.ft_utm_campaign, 200),
    ft_utm_term: str(p.ft_utm_term, 200),
    ft_utm_content: str(p.ft_utm_content, 200),
    gclid: str(p.gclid, 200),
    fbclid: str(p.fbclid, 200),
    msclkid: str(p.msclkid, 200),
    ttclid: str(p.ttclid, 200),
    referrer: str(p.referrer, 300),
    landing_page: str(p.landing_path ?? p.landing_page, 300),
    page_url: str(p.page_url),
    query_string: str(p.query_string),
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : null,
    // Keep the raw payload so nothing is lost if a new param shows up later.
    attribution: JSON.parse(JSON.stringify(p)) as Json,
  };
}

/** Result of an atomic register-or-merge. */
export type MasterclassRegistrationResult = {
  id: string | null;
  /** False when an existing registration for this email + masterclass was merged. */
  isNew: boolean;
  submissionCount: number;
};

/**
 * Persist a masterclass registration.
 *
 * Uniqueness (one row per masterclass + email) is enforced by a unique index
 * and an atomic upsert inside `register_masterclass`, so two requests that
 * arrive at the same instant can never create two registrations — the loser
 * of the race merges into the winner's row.
 */
export async function registerMasterclass(
  payload: Payload,
): Promise<MasterclassRegistrationResult> {
  const attribution = attributionColumns(payload);
  const { data, error } = await supabase.rpc("register_masterclass", {
    _payload: {
      masterclass_key: str(payload.masterclass_key, 120) ?? "default",
      full_name: str(payload.name ?? payload.fullName, 120) ?? "Unknown",
      email: (str(payload.email, 255) ?? "").toLowerCase(),
      phone: str(payload.phone, 40),
      goal: str(payload.goal, 1000),
      source: str(payload.source, 120),
      page: str(payload.page ?? payload.page_path, 200),
      submitted_at: str(payload.submitted_at) ?? new Date().toISOString(),
      ...attribution,
    } as unknown as Json,
  });

  if (error) {
    console.warn("[leads] masterclass store failed:", error.message);
    return { id: null, isNew: false, submissionCount: 0 };
  }

  const row = (data ?? {}) as { id?: string; is_new?: boolean; submission_count?: number };
  return {
    id: row.id ?? null,
    isNew: Boolean(row.is_new),
    submissionCount: row.submission_count ?? 0,
  };
}

/** Back-compat wrapper: returns just the lead id. */
export async function storeMasterclassRegistration(payload: Payload): Promise<string | null> {
  return (await registerMasterclass(payload)).id;
}

export async function storeStrategySessionSubmission(payload: Payload): Promise<string | null> {
  const { data, error } = await supabase.from("strategy_session_submissions").insert({
    full_name: str(payload.fullName ?? payload.name, 120) ?? "Unknown",
    email: (str(payload.email, 255) ?? "").toLowerCase(),
    phone: str(payload.phone, 40),
    organization: str(payload.organization, 200),
    role_title: str(payload.role ?? payload.role_title, 200),
    focus: str(payload.focus, 200),
    current_stage: str(payload.currentStage ?? payload.current_stage, 200),
    desired_outcome: str(payload.desiredOutcome ?? payload.desired_outcome, 2000),
    timeline: str(payload.timeline, 200),
    budget: str(payload.budget, 200),
    decision_authority: str(payload.decisionRole ?? payload.decision_authority, 200),
    situation: str(payload.situation, 4000),
    timezone: str(payload.timezone, 64),
    time_on_form_ms:
      typeof payload.time_on_form_ms === "number" ? Math.min(payload.time_on_form_ms, 2147483647) : null,
    source: str(payload.source, 120),
    page: str(payload.page ?? payload.page_path, 200),
    submitted_at: str(payload.submitted_at) ?? new Date().toISOString(),
    ...attributionColumns(payload),
  }).select("id").maybeSingle();
  // 23505 = the backend dedupe rules already merged this into an existing lead.
  if (error && error.code !== "23505") console.warn("[leads] strategy session store failed:", error.message);
  return data?.id ?? null;
}
